import { formatPhoneNumber } from 'util/formatPhoneNumber';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useOrganizationsApi, Organization } from 'services/api';
import type { DateFilterPreset } from 'hooks/useAnalyticsDateFilter';
import { useProfile } from 'hooks';
import { RoleMap } from 'hooks/useGetRole';
import { EditRoleModal } from '../EditRoleModal';
import { RemoveAdminModal } from '../RemoveAdminModal';
import { RemoveLeaderModal } from '../RemoveLeaderModal';
import {
  buildOrgMemberRows,
  deriveLeaderUiStatus,
  leaderStatusLabel,
  OrgMemberRow,
} from './deriveLeaderUiStatus';
import { formatLeaderDate } from './formatLeaderDates';
import { LeaderDetailBottomSheet } from './LeaderDetailBottomSheet';
import { LeadershipPagination } from './LeadershipPagination';
import { LeadershipToolbar } from './LeadershipToolbar';
import { OrganizationLeadersDesktopTable } from './OrganizationLeadersDesktopTable';
import { OrganizationLeadersMobileCards } from './OrganizationLeadersMobileCards';

const PAGE_SIZE = 10;

function escapeCsvCell (value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function downloadOrgLeadersCsv (
  rows: OrgMemberRow[],
  organizationName: string,
): void {
  const headers = [
    'Name',
    'Email',
    'Phone',
    'Status',
    'Role',
    'Date Joined',
    'Last Seen',
  ];
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const r of rows) {
    const status = leaderStatusLabel(deriveLeaderUiStatus(r));
    const phone = r.phoneNumber ? formatPhoneNumber(r.phoneNumber) : '';
    lines.push(
      [
        `${r.firstName} ${r.lastName}`.trim(),
        r.schoolEmail,
        phone,
        status,
        r.orgRole || '',
        formatLeaderDate(r.createdAt),
        formatLeaderDate(r.lastSeen),
      ]
        .map(c => escapeCsvCell(String(c)))
        .join(','),
    );
  }
  const csv = lines.join('\n');
  const safeName = organizationName.replace(/[^\w-]+/g, '_').slice(0, 80);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName || 'organization'}-leaders.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface OrganizationLeadersTabProps {
  organizationId: string;
  organizationName: string;
  orgLeaders: Organization['orgLeaders'] | undefined;
  orgAdmins: Organization['admins'] | undefined;
  pendingAdminRequests: Organization['pendingAdminRequests'] | undefined;
  onRefresh: () => Promise<void>;
  onReInvite: (leaderId: string) => Promise<void>;
  onAddLeader: () => void;
  dateFilterPreset: DateFilterPreset;
  dateFilterStartDate: Date | null;
  dateFilterEndDate: Date | null;
  onOpenDateFilter: () => void;
}

export function OrganizationLeadersTab ({
  organizationId,
  organizationName,
  orgLeaders,
  orgAdmins,
  pendingAdminRequests,
  onRefresh,
  onReInvite,
  onAddLeader,
  dateFilterPreset,
  dateFilterStartDate,
  dateFilterEndDate,
  onOpenDateFilter,
}: OrganizationLeadersTabProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const organizationsApi = useOrganizationsApi();
  const { profile } = useProfile();

  const canRemoveAdmin = useMemo(() => {
    if (!profile) return false;
    if (profile.role.includes(RoleMap['executive-admin'])) return true;
    const isOrgAdmin = (profile.adminOrganizations ?? []).some(
      org => org.id === organizationId,
    );
    return profile.role.includes(RoleMap.admin) && isOrgAdmin;
  }, [profile, organizationId]);

  const [page, setPage] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);
  const [sheetMember, setSheetMember] = useState<OrgMemberRow | null>(null);
  const [removeTarget, setRemoveTarget] = useState<OrgMemberRow | null>(null);
  const [editModal, setEditModal] = useState<{
    leaderId: string;
    currentRole: string;
    pendingUserInitiated: boolean;
  } | null>(null);

  const sorted = useMemo(
    () => buildOrgMemberRows(orgLeaders, orgAdmins, pendingAdminRequests),
    [orgLeaders, orgAdmins, pendingAdminRequests],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  useEffect(() => {
    setPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, safePage]);

  const handleExport = () => {
    if (!sorted.length) return;
    setExportLoading(true);
    try {
      downloadOrgLeadersCsv(sorted, organizationName);
      enqueueSnackbar('Leaders list exported', { variant: 'success' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleCancel = async (member: OrgMemberRow) => {
    if (member.kind !== 'leader') return;
    try {
      const res = await organizationsApi.cancelAdminLeaderInvite(
        organizationId,
        member.id,
      );
      if (!res.success) throw new Error(res.error.message);
      enqueueSnackbar('Invitation withdrawn', { variant: 'success' });
      await onRefresh();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Request failed';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const openEdit = (member: OrgMemberRow) => {
    if (member.kind !== 'leader') return;
    const ui = deriveLeaderUiStatus(member);
    setEditModal({
      leaderId: member.id,
      currentRole: member.orgRole || '',
      pendingUserInitiated: ui === 'pendingAdminApproval',
    });
  };

  const closeRemoveTarget = () => setRemoveTarget(null);
  const onRemoveSuccess = async () => {
    await onRefresh();
    setRemoveTarget(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '32px', md: 0 }, width: '100%' }}>
      <LeadershipToolbar
        onExport={handleExport}
        exportDisabled={!sorted.length}
        exportLoading={exportLoading}
        onAddLeader={onAddLeader}
        dateFilterPreset={dateFilterPreset}
        dateFilterStartDate={dateFilterStartDate}
        dateFilterEndDate={dateFilterEndDate}
        onOpenDateFilter={onOpenDateFilter}
      />

      {!sorted.length ? (
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          No organization leaders yet.
        </Typography>
      ) : isMobile ? (
        <OrganizationLeadersMobileCards
          leaders={paged}
          onRowPress={m => setSheetMember(m)}
        />
      ) : (
        <OrganizationLeadersDesktopTable
          leaders={paged}
          canRemoveAdmin={canRemoveAdmin}
          onEdit={openEdit}
          onRemove={m => setRemoveTarget(m)}
          onCancel={handleCancel}
          onReInvite={async m => {
            if (m.kind !== 'leader') return;
            await onReInvite(m.id);
          }}
        />
      )}

      {sorted.length > 0 ? (
        <LeadershipPagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      ) : null}

      {isMobile ? (
        <LeaderDetailBottomSheet
          open={!!sheetMember}
          leader={sheetMember}
          canRemoveAdmin={canRemoveAdmin}
          onClose={() => setSheetMember(null)}
          onEdit={openEdit}
          onRemove={m => setRemoveTarget(m)}
          onCancel={handleCancel}
          onReInvite={async m => {
            if (m.kind !== 'leader') return;
            await onReInvite(m.id);
          }}
        />
      ) : null}

      <EditRoleModal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        leaderId={editModal?.leaderId}
        currentRole={editModal?.currentRole}
        pendingUserInitiatedRequest={editModal?.pendingUserInitiated}
        onSuccess={async () => {
          await onRefresh();
          setEditModal(null);
        }}
      />

      {removeTarget?.kind === 'leader' ? (
        <RemoveLeaderModal
          open
          onClose={closeRemoveTarget}
          organizationId={organizationId}
          userId={removeTarget.userId}
          leaderDisplayName={`${removeTarget.firstName} ${removeTarget.lastName}`.trim()}
          leaderOrgRole={(removeTarget.orgRole || '').trim() || 'Leader'}
          onSuccess={onRemoveSuccess}
        />
      ) : null}

      {removeTarget?.kind === 'admin' ? (
        <RemoveAdminModal
          open
          onClose={async () => {
            closeRemoveTarget();
            await onRefresh();
          }}
          organizationId={organizationId}
          userId={removeTarget.userId}
        />
      ) : null}
    </Box>
  );
}
